import { Link, useParams } from "react-router-dom";
import { categories, reviews } from "@/mocks/category";

export function CategoryHero() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[550px] flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url(https://readdy.ai/api/search-image?query=Dark%20dramatic%20theater%20stage%20with%20crimson%20red%20curtains%2C%20empty%20stage%20with%20single%20spotlight%2C%20deep%20black%20and%20dark%20red%20cinematic%20color%20grade%2C%20mysterious%20atmosphere%2C%20theatrical%20lighting%2C%20fine%20art%20photography%2C%20dramatic%20shadows%2C%20professional%20color%20grade%2C%20luxurious%20velvet%20texture%2C%20high%20contrast%20noir%20aesthetic&width=1920&height=1080&seq=cat-hero-bg&orientation=landscape)",
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>

      <div className="relative z-10 w-full px-4 md:px-6 flex flex-col items-center justify-center text-center pt-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 mb-4">
            {category.name}
          </h1>
          <p className="text-sm md:text-lg text-foreground-300 mb-6 max-w-xl mx-auto leading-relaxed">
            {category.description}
          </p>
          <p className="text-lg md:text-xl text-primary-400 italic font-light">
            "{category.tagline}"
          </p>
        </div>
      </div>
    </section>
  );
}

export function CategoryGrid() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <section className="py-20 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto text-center">
          <p className="text-foreground-500 text-lg">分类不存在</p>
          <Link
            to="/"
            className="whitespace-nowrap mt-4 inline-block px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-foreground-50 font-medium rounded-full transition-all duration-200"
          >
            返回首页
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {category.items.map((item, index) => (
            <Link
              key={item.id}
              to={`/article/${item.articleId}`}
              className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-[1.02] ${
                item.featured && index === 0
                  ? "md:col-span-2 md:row-span-2"
                  : ""
              }`}
            >
              <div
                className={`w-full ${
                  item.featured && index === 0 ? "aspect-[16/10]" : "aspect-[4/3]"
                }`}
              >
                <img
                  src={item.imageUrl}
                  alt={`${item.title} - ${category.name}`}
                  title={`${item.title} - ${category.name}`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>

              {item.tag && (
                <span className="absolute top-3 left-3 whitespace-nowrap px-2.5 py-1 text-xs font-semibold bg-accent-500 text-background-950 rounded-full flex items-center gap-1">
                  <i className="ri-fire-line text-xs"></i>
                  {item.tag}
                </span>
              )}

              <div className="absolute bottom-0 left-0 p-5 md:p-6 w-full">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-1 leading-snug">
                  {item.title}
                </h3>
                <p className="text-xs text-foreground-500 mb-2">{item.date}</p>
                <p className="text-sm text-foreground-400 leading-relaxed hidden md:block">
                  {item.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CategoryReviews() {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 text-center mb-3">
          吃瓜群众怎么说
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-10 md:mb-14">
          看看广大瓜友的真实评价
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-background-100 rounded-xl p-5 md:p-6 flex flex-col gap-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={review.avatarUrl}
                    alt={review.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-100">
                    {review.name}
                  </p>
                  <p className="text-xs text-foreground-500">
                    {review.username}
                  </p>
                </div>
              </div>
              <p className="text-sm text-foreground-400 leading-relaxed italic">
                "{review.quote}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}